import { useCallback, useState } from "react";
import { useMotivoAfastamentoFilters } from "../hooks/MotivoAfastamento_lista/filters";
import { useMotivoAfastamentoPagination } from "../hooks/MotivoAfastamento_lista/pagination";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { MotivoAfastamento } from "../../domain/models/motivo_afastamento";
import { MotivoAfastamentoForm } from "../schemas/MotivoAfastamento_lista/motivo_afastamento";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * useMotivoAfastamentoListaViewModel
 */

type LoadMotivoAfastamentoState = {
  tipoPesquisa?: number;
  motivoAfastamento?: string;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useMotivoAfastamentoListaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    rh: {
      getMotivosAfastamentoUseCase,
      createMotivoAfastamentoUseCase,
      updateMotivoAfastamentoUseCase,
      deleteMotivoAfastamentoUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedMotivoAfastamento, setSelectedMotivoAfastamento] = useState<MotivoAfastamento>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [motivosAfastamento, setMotivosAfastamento] = useState<MotivoAfastamento[]>();

  const filters = useMotivoAfastamentoFilters();
  const pagination = useMotivoAfastamentoPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: error => pushErrorNotification("Erro", [error]),
  });

  /**
   * Funções de carregamento
   */

  const loadMotivoAfastamento = useCallback(async (funcState?: LoadMotivoAfastamentoState) => {
    try {
      const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
      const filteredMotivoAfastamento = funcState?.motivoAfastamento ?? filters.motivoAfastamento;
      const filteredStatus = funcState?.status ?? filters.status;

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getMotivosAfastamentoUseCase.getMotivosAfastamento(
        filteredTipoPesquisa,
        filteredMotivoAfastamento,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setMotivosAfastamento(getMotivosAfastamentoUseCase.items);

      pagination.setRowsPerPage(getMotivosAfastamentoUseCase.pageSize);
      pagination.setTotalItems(getMotivosAfastamentoUseCase.totalItems);
    } catch (e: unknown) {
      setMotivosAfastamento([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getMotivosAfastamentoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadMotivoAfastamento();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar motivos de afastamento!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadMotivoAfastamento();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMotivoAfastamento,
    pushErrorNotification,
  ]);

  const handleSubmitMotivoAfastamento = useCallback(async (form: MotivoAfastamentoForm) => {
    if (!loading && !saving) {
      try {
        setSaving(true);

        if (selectedMotivoAfastamento) {
          await updateMotivoAfastamentoUseCase.updateMotivoAfastamento(
            selectedMotivoAfastamento.idMotivoAfastamento,
            form.ativo,
            form.descricaoMotivo,
          );
        } else {
          await createMotivoAfastamentoUseCase.createMotivoAfastamento(
            form.ativo,
            form.descricaoMotivo,
          );
        }

        await loadMotivoAfastamento();
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao criar motivo de afastamento!"));
      } finally {
        setSaving(false);
        setOpenCadastroModal(false);
        setSelectedMotivoAfastamento(undefined);
      }
    }
  }, [
    loading,
    saving,
    selectedMotivoAfastamento,
    createMotivoAfastamentoUseCase,
    updateMotivoAfastamentoUseCase,
    loadMotivoAfastamento,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (tipoPesquisa: number) => {
    useMotivoAfastamentoFilters.getState().setTipoPesquisa(tipoPesquisa);
    return async () => {
      try {
        setLoading(true);
        useMotivoAfastamentoPagination.getState().setPage(1);
        await loadMotivoAfastamento({ tipoPesquisa, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
  ]);

  const handleMotivoAfastamentoChange = useDebouncedCallback(debouncedRef, (motivoAfastamento: string) => {
    useMotivoAfastamentoFilters.getState().setMotivoAfastamento(motivoAfastamento);
    return async () => {
      try {
        setLoading(true);
        useMotivoAfastamentoPagination.getState().setPage(1);
        await loadMotivoAfastamento({ motivoAfastamento, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
    useMotivoAfastamentoFilters.getState().setStatus(status);
    return async () => {
      try {
        setLoading(true);
        useMotivoAfastamentoPagination.getState().setPage(1);
        await loadMotivoAfastamento({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadMotivoAfastamento,
    pushErrorNotification,
  ]);

  const handleMotivoAfastamentoCadastroOpenState = useCallback((idMotivoAfastamento: number | null, openState: boolean) => {
    if (openState) {
      if (idMotivoAfastamento && motivosAfastamento) {
        setSelectedMotivoAfastamento(motivosAfastamento.find(x => x.idMotivoAfastamento === idMotivoAfastamento));
      } else {
        setSelectedMotivoAfastamento(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [motivosAfastamento]);

  const handleMotivoAfastamentoDeleteOpenState = useCallback((idMotivoAfastamento: number | null, openState: boolean) => {
    if (openState) {
      if (idMotivoAfastamento && motivosAfastamento) {
        setSelectedMotivoAfastamento(motivosAfastamento.find(x => x.idMotivoAfastamento === idMotivoAfastamento));
      } else {
        setSelectedMotivoAfastamento(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [motivosAfastamento]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useMotivoAfastamentoPagination.getState().setPage(page + 1);
      await loadMotivoAfastamento({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMotivoAfastamento,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useMotivoAfastamentoPagination.getState().setPage(1);
      useMotivoAfastamentoPagination.getState().setRowsPerPage(rowsPerPage);
      await loadMotivoAfastamento({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadMotivoAfastamento,
    pushErrorNotification,
  ]);

  const handleConfirmarDeletarMotivoAfastamento = useCallback(async () => {
    if (selectedMotivoAfastamento) {
      try {
        setSaving(true);
        await deleteMotivoAfastamentoUseCase.deleteMotivoAfastamento(selectedMotivoAfastamento.idMotivoAfastamento);
        await loadMotivoAfastamento();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de afastamento!'));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
        setSelectedMotivoAfastamento(undefined);
      }
    }
  }, [
    selectedMotivoAfastamento,
    deleteMotivoAfastamentoUseCase,
    loadMotivoAfastamento,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedMotivoAfastamento) {
      changeHistory.handleOpenChangeHistory(selectedMotivoAfastamento.idMotivoAfastamento, LogTipoTabela.MOTIVO_AFASTAMENTO, false);
    }
  }, [
    selectedMotivoAfastamento,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    selectedMotivoAfastamento,
    openCadastroModal,
    openDeleteModal,
    errorMessage,
    motivosAfastamento: motivosAfastamento ?? [],
    filters,
    pagination,
    changeHistory,
    handleReload,
    handleSubmitMotivoAfastamento,
    handleTipoPesquisaChange,
    handleMotivoAfastamentoChange,
    handleStatusChange,
    handleMotivoAfastamentoCadastroOpenState,
    handleMotivoAfastamentoDeleteOpenState,
    handlePageChange,
    handleRowsPerPageChange,
    handleConfirmarDeletarMotivoAfastamento,
    handleOpenChangeHistory,
  };
}